import { useCallback } from "react";
import { useNostr } from "@nostrify/react";

import { useLocalStorage } from "./useLocalStorage";
import { useCurrentUser } from "./useCurrentUser";
import { ORDER_RELAYS } from "@/lib/cashcraft";

const STORAGE_KEY = "bchlaunch:dismissed-orders";

/**
 * Tracks orders the operator has "deleted" (dismissed) from their inbox.
 *
 * Dismissed order ids are stored locally so they stay hidden across reloads.
 * We also broadcast a best-effort NIP-09 deletion request — relays only honor
 * deletions from the original author (orders are signed by throwaway keys, so
 * this won't always purge them), but the local hide reliably cleans the inbox.
 */
export function useDismissedOrders() {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const [dismissed, setDismissed] = useLocalStorage<string[]>(STORAGE_KEY, []);

  const isDismissed = useCallback(
    (orderId: string) => dismissed.includes(orderId),
    [dismissed],
  );

  const dismiss = useCallback(
    async (orderId: string) => {
      setDismissed((prev) =>
        prev.includes(orderId) ? prev : [...prev, orderId],
      );

      // Best-effort NIP-09 deletion broadcast.
      try {
        if (user) {
          const event = await user.signer.signEvent({
            kind: 5,
            content: "Dismissed test/duplicate order",
            tags: [["e", orderId]],
            created_at: Math.floor(Date.now() / 1000),
          });
          await Promise.allSettled([
            nostr.group(ORDER_RELAYS).event(event, {
              signal: AbortSignal.timeout(5000),
            }),
            nostr.event(event, { signal: AbortSignal.timeout(5000) }),
          ]);
        }
      } catch {
        // Local hide already applied; ignore broadcast failures.
      }
    },
    [setDismissed, user, nostr],
  );

  const restoreAll = useCallback(() => setDismissed([]), [setDismissed]);

  return { dismissed, isDismissed, dismiss, restoreAll };
}
